import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { usePendingCountsStore } from './pendingCountsStore'
import { apiService } from '@/services/api'

interface Notification {
  id: number
  type: string
  title: string
  content: string
  reference_id: number | null
  created_at: string
  is_read: boolean
}

// 알림 타입별 소리 설정 타입
export type NotificationSoundSettings = {
  [key: string]: string | null  // 알림 타입 -> 소리 파일 URL
}

// 안읽은 채팅 반복 알람 설정
export interface ChatUnreadAlarmConfig {
  enabled: boolean
  intervalMinutes: number
}

interface NotificationStore {
  // State
  notifications: Notification[]
  unreadCount: number
  isConnected: boolean
  websocket: WebSocket | null
  reconnectAttempts: number
  isLeaderTab: boolean
  broadcastChannel: BroadcastChannel | null
  customSoundUrl: string | null
  soundSettings: NotificationSoundSettings
  chatUnreadAlarm: ChatUnreadAlarmConfig

  // Actions
  connect: (adminId: number) => void
  disconnect: () => void
  markAsRead: (notificationId: number) => void
  markAllAsRead: () => void
  playNotificationSound: (message: string, notificationType?: string) => void
  loadSoundSettings: () => Promise<void>  // 서버에서 소리 설정 로드
  setCustomSoundUrl: (url: string | null) => void  // 기본 커스텀 소리 파일 URL 설정 (서버 저장)
  setSoundForType: (type: string, url: string | null) => void  // 특정 알림 타입의 소리 설정 (서버 저장)
  getSoundForType: (type: string) => string | null  // 특정 알림 타입의 소리 가져오기
  setChatUnreadAlarm: (cfg: ChatUnreadAlarmConfig) => Promise<void>  // 안읽은 채팅 반복 알람 설정 (서버 저장)

  // Internal
  setNotifications: (notifications: Notification[]) => void
  addNotification: (notification: Notification) => void
  setConnectionStatus: (connected: boolean) => void
}

/* ── 공유 오디오 엔진 (2026-07-19 백그라운드 무음 수정) ──
   기존엔 재생 때마다 new AudioContext() → 상호작용 없는 세션/백그라운드 탭에서 suspended로 시작해
   resume 실패 → 관리자마다 간헐 무음. 단일 컨텍스트를 제스처/화면복귀 때마다 resume해 공유. */
let sharedCtx: AudioContext | null = null
const getSharedCtx = (): AudioContext | null => {
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext
    if (!AC) return null
    if (!sharedCtx) sharedCtx = new AC()
    return sharedCtx
  } catch { return null }
}
const resumeSharedCtx = (): void => {
  const c = getSharedCtx()
  if (c && c.state !== 'running') { void c.resume().catch(() => { /* */ }) }
}
if (typeof window !== 'undefined') {
  ;['click', 'keydown', 'touchstart'].forEach(ev =>
    window.addEventListener(ev, () => {
      resumeSharedCtx()
      // 데스크톱 알림 권한 — 첫 제스처에서 1회 요청 (다른 창 볼 때 OS 배너용)
      try {
        if ('Notification' in window && Notification.permission === 'default') {
          void Notification.requestPermission()
        }
      } catch { /* */ }
    }, { passive: true }))
  document.addEventListener('visibilitychange', () => { if (!document.hidden) resumeSharedCtx() })
}
// 커스텀 사운드 — Web Audio 버퍼 캐시(백그라운드 탭에서도 재생 가능), 실패 시 HTMLAudio 폴백
const soundBufCache: Record<string, AudioBuffer> = {}
const soundBufLoading: Record<string, boolean> = {}
const preloadSoundBuffer = (url: string): void => {
  const c = getSharedCtx()
  if (!c || soundBufCache[url] || soundBufLoading[url]) return
  soundBufLoading[url] = true
  fetch(url)
    .then(r => r.arrayBuffer())
    .then(arr => new Promise<AudioBuffer>((res, rej) => { c.decodeAudioData(arr, res, rej) }))
    .then(buf => { soundBufCache[url] = buf })
    .catch(() => { soundBufLoading[url] = false })
}
const playUrlViaWebAudio = (url: string): boolean => {
  try {
    const c = getSharedCtx()
    if (!c || !soundBufCache[url]) { preloadSoundBuffer(url); return false }
    if (c.state !== 'running') { void c.resume().catch(() => { /* */ }) }
    if (c.state !== 'running') return false
    const src = c.createBufferSource()
    src.buffer = soundBufCache[url]
    const g = c.createGain()
    g.gain.value = 1.0
    src.connect(g); g.connect(c.destination)
    src.start(0)
    return true
  } catch { return false }
}
// 다른 창/탭을 보고 있을 때 OS 데스크톱 알림 배너 (권한 허용 시)
const showDesktopNotification = (message: string): void => {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return
    if (!document.hidden && document.hasFocus()) return   // 지금 보고 있으면 소리로 충분
    const n = new Notification('EVEN 관리자 알림', { body: message, tag: 'even-admin-notif', silent: false })
    n.onclick = () => { try { window.focus(); n.close() } catch { /* */ } }
  } catch { /* */ }
}

export const useNotificationStore = create<NotificationStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    websocket: null,
    reconnectAttempts: 0,
    isLeaderTab: false,
    broadcastChannel: null,
    customSoundUrl: null,
    soundSettings: {} as NotificationSoundSettings,
    chatUnreadAlarm: { enabled: false, intervalMinutes: 1 },

    // Connect to WebSocket
    connect: (adminId: number) => {
      console.log('🔔 connect 함수 호출됨:', { adminId, timestamp: new Date().toISOString() })
      
      const { websocket, broadcastChannel } = get()

      // adminId가 없으면 연결하지 않음
      if (!adminId) {
        console.error('🔔 WebSocket 연결 실패: adminId가 없습니다', { adminId })
        return
      }

      // 이미 연결되어 있으면 스킵
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        console.log('🔔 이미 웹소켓이 연결되어 있습니다:', adminId)
        return
      }

      // Close existing connection
      if (websocket) {
        console.log('🔔 기존 웹소켓 연결 종료')
        websocket.close()
      }

      // 공유 AudioContext 준비 — 제스처/화면복귀 리스너(모듈 레벨)가 자동으로 resume 유지
      resumeSharedCtx()

      // BroadcastChannel 생성 (탭 간 통신)
      const bc = new BroadcastChannel('admin_notifications')

      // 리더 탭 선출 (먼저 연결된 탭이 리더)
      let isLeader = true
      bc.postMessage({ type: 'leader_check' })

      bc.onmessage = (event) => {
        if (event.data.type === 'leader_check') {
          // 다른 탭이 이미 있으면 현재 탭은 follower
          isLeader = false
          set({ isLeaderTab: false })
          bc.postMessage({ type: 'leader_response' })
        } else if (event.data.type === 'leader_response' && isLeader) {
          // 응답이 오면 이미 다른 리더가 있음
          isLeader = false
          set({ isLeaderTab: false })
        } else if (event.data.type === 'new_notification') {
          // 다른 탭에서 받은 알림 동기화 (소리 없이)
          const { addNotification } = get()
          addNotification(event.data.notification)
        }
      }

      // 100ms 후 아직도 리더면 확정
      setTimeout(() => {
        if (isLeader) {
          set({ isLeaderTab: true })
          console.log('🎯 This tab is the LEADER for notifications')
        }
      }, 100)

      set({ broadcastChannel: bc })

      // 서버에서 소리 설정 로드
      get().loadSoundSettings()

      // Create new WebSocket connection - 백엔드 서버로 연결 (admin JWT 토큰 query 추가)
      const adminToken = localStorage.getItem('admin_token') || ''
      const wsUrl = `${(import.meta as any).env.VITE_WS_URL || ((window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//api.kryzonic.com')}/api/v1/admin/notifications/ws/${adminId}?token=${encodeURIComponent(adminToken)}`
      console.log('🔔 Attempting to connect to notification WebSocket:', wsUrl, 'adminId:', adminId)
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('🔔 Admin notification WebSocket connected', { adminId, url: wsUrl })
        set({ isConnected: true, websocket: ws, reconnectAttempts: 0 })
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('🔔 WebSocket message received:', data)

          switch (data.type) {
            case 'initial':
              // Initial notifications load
              set({
                notifications: data.notifications || [],
                unreadCount: data.unread_count || 0
              })
              break

            case 'new_notification':
              // New notification received
              const { addNotification, playNotificationSound, isLeaderTab, broadcastChannel } = get()
              addNotification(data.notification)

              // 알림 타입 추출 (notification.type 또는 data.notification.type)
              const notificationType = data.notification?.type || data.type || 'default'

              console.log('🔔 New notification received:', {
                sound: data.sound,
                isLeaderTab: isLeaderTab,
                title: data.notification.title,
                type: notificationType
              })

              // 알림 타입에 따라 pending counts 증가
              const pendingCountsStore = usePendingCountsStore.getState()
              console.log('📊 Updating pending counts for type:', notificationType, 'current counts:', {
                pendingChats: pendingCountsStore.pendingChats,
                pendingDeposits: pendingCountsStore.pendingDeposits,
                pendingWithdrawals: pendingCountsStore.pendingWithdrawals,
                pendingInquiries: pendingCountsStore.pendingInquiries,
                pendingRegistrations: pendingCountsStore.pendingRegistrations,
                pendingPartnerWithdrawals: pendingCountsStore.pendingPartnerWithdrawals
              })
              
              switch (notificationType) {
                case 'chat':
                  // 채팅 알림이 오면 pendingChats 증가
                  pendingCountsStore.setPendingCounts({
                    pendingChats: (pendingCountsStore.pendingChats || 0) + 1
                  })
                  console.log('📊 pendingChats increased to:', (pendingCountsStore.pendingChats || 0) + 1)
                  break
                case 'deposit':
                  // 입금 신청 알림이 오면 pendingDeposits 증가
                  pendingCountsStore.setPendingCounts({
                    pendingDeposits: (pendingCountsStore.pendingDeposits || 0) + 1
                  })
                  console.log('📊 pendingDeposits increased to:', (pendingCountsStore.pendingDeposits || 0) + 1)
                  break
                case 'withdrawal':
                  // 출금 신청 알림이 오면 pendingWithdrawals 증가
                  pendingCountsStore.setPendingCounts({
                    pendingWithdrawals: (pendingCountsStore.pendingWithdrawals || 0) + 1
                  })
                  console.log('📊 pendingWithdrawals increased to:', (pendingCountsStore.pendingWithdrawals || 0) + 1)
                  break
                case 'inquiry':
                  // 문의 알림이 오면 pendingInquiries 증가
                  pendingCountsStore.setPendingCounts({
                    pendingInquiries: (pendingCountsStore.pendingInquiries || 0) + 1
                  })
                  console.log('📊 pendingInquiries increased to:', (pendingCountsStore.pendingInquiries || 0) + 1)
                  break
                case 'user_registration':
                  // 신규 가입 알림이 오면 pendingRegistrations 증가
                  pendingCountsStore.setPendingCounts({
                    pendingRegistrations: (pendingCountsStore.pendingRegistrations || 0) + 1
                  })
                  console.log('📊 pendingRegistrations increased to:', (pendingCountsStore.pendingRegistrations || 0) + 1)
                  break
                case 'partner_withdrawal':
                  // 파트너 출금 알림이 오면 pendingPartnerWithdrawals 증가
                  pendingCountsStore.setPendingCounts({
                    pendingPartnerWithdrawals: (pendingCountsStore.pendingPartnerWithdrawals || 0) + 1
                  })
                  console.log('📊 pendingPartnerWithdrawals increased to:', (pendingCountsStore.pendingPartnerWithdrawals || 0) + 1)
                  break
              }

              // 다른 탭에 알림 브로드캐스트
              if (broadcastChannel) {
                broadcastChannel.postMessage({
                  type: 'new_notification',
                  notification: data.notification
                })
              }

              // 알림 소리 재생 (백그라운드에서도 작동)
              if (data.sound) {
                console.log('🔊 Attempting to play notification sound...', {
                  isLeaderTab,
                  hasBroadcastChannel: !!broadcastChannel,
                  visibilityState: document.visibilityState,
                  notificationType
                })
                
                // 모든 탭에서 playNotificationSound 호출
                // 리더 탭에서만 TTS 재생, 모든 탭에서 소리 재생
                playNotificationSound(data.notification.title, notificationType)
              }
              break
              
            case 'mark_read':
              // Notification marked as read
              set((state) => ({
                notifications: state.notifications.map(n =>
                  n.id === data.notification_id ? { ...n, is_read: true } : n
                ),
                unreadCount: Math.max(0, state.unreadCount - 1)
              }))
              break
              
            case 'mark_all_read':
              // All notifications marked as read
              set((state) => ({
                notifications: state.notifications.map(n => ({ ...n, is_read: true })),
                unreadCount: 0
              }))
              break
              
            case 'pong':
              // Connection heartbeat response
              console.log('📡 WebSocket heartbeat received')
              break
              
            default:
              console.warn('Unknown notification message type:', data.type)
          }
        } catch (error) {
          console.error('Failed to parse notification message:', error)
        }
      }
      
      ws.onclose = (event) => {
        console.log('🔔 Admin notification WebSocket disconnected', event.code, event.reason)
        set({ isConnected: false, websocket: null })
        
        // Only reconnect if it wasn't a manual disconnect (code 1000 or 1001)
        if (event.code !== 1000 && event.code !== 1001) {
          // Exponential backoff for reconnection (5s, 10s, 20s, max 60s)
          const state = get()
          const reconnectDelay = Math.min(5000 * Math.pow(2, (state.reconnectAttempts || 0)), 60000)
          
          console.log(`🔄 Reconnecting in ${reconnectDelay/1000}s...`)
          setTimeout(() => {
            const currentState = get()
            if (!currentState.isConnected) {
              get().connect(adminId)
              set({ reconnectAttempts: (currentState.reconnectAttempts || 0) + 1 })
            }
          }, reconnectDelay)
        }
      }
      
      ws.onerror = (error) => {
        console.error('🔔 Admin notification WebSocket error:', error, 'URL:', wsUrl, 'adminId:', adminId)
        set({ isConnected: false })
      }
    },
    
    // Disconnect WebSocket
    disconnect: () => {
      const { websocket, broadcastChannel } = get()
      if (websocket) {
        websocket.close()
      }
      if (broadcastChannel) {
        broadcastChannel.close()
      }
      set({ websocket: null, broadcastChannel: null, isConnected: false, isLeaderTab: false })
    },
    
    // Mark notification as read
    markAsRead: (notificationId: number) => {
      const { websocket } = get()
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({
          type: 'mark_read',
          notification_id: notificationId
        }))
      }
    },
    
    // Mark all notifications as read
    markAllAsRead: () => {
      const { websocket } = get()
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({
          type: 'mark_all_read'
        }))
      }
    },
    
    // 서버에서 소리 설정 로드
    loadSoundSettings: async () => {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.kryzonic.com'
        const response = await apiService.get<{
          custom_sound_url: string | null
          sound_settings: { [key: string]: string | null }
          chat_unread_alarm?: { enabled: boolean; interval_minutes: number }
        }>('/api/v1/admin/notices/sound-settings')
        
        console.log('🔊 API response:', response)
        
        // apiService.get()은 이미 response.data를 반환하므로, response 자체가 { result, message, data } 형식
        if (response && response.result === 'success' && response.data) {
          // URL 포트 제거 처리
          const getSoundUrl = (url: string | null): string | null => {
            if (!url) return null
            if (url.startsWith('http')) {
              return url.replace(':8000', '').replace(':3001', '')
            }
            const baseUrl = apiBaseUrl
            return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`
          }
          
          const customSoundUrl = getSoundUrl(response.data.custom_sound_url)
          const soundSettings: NotificationSoundSettings = {}
          if (response.data.sound_settings) {
            for (const [type, url] of Object.entries(response.data.sound_settings)) {
              soundSettings[type] = getSoundUrl(url as string | null)
            }
          }
          
          // 안읽은 채팅 반복 알람 설정
          const alarm = response.data.chat_unread_alarm
          const chatUnreadAlarm: ChatUnreadAlarmConfig = {
            enabled: !!(alarm && alarm.enabled),
            intervalMinutes: Math.max(1, Math.min(60, Number(alarm?.interval_minutes) || 1)),
          }

          set({
            customSoundUrl,
            soundSettings,
            chatUnreadAlarm,
          })
          console.log('🔊 Sound settings loaded from server:', { customSoundUrl, soundSettings, chatUnreadAlarm })
        } else {
          console.log('🔊 No sound settings found on server, using defaults. Response:', response)
        }
      } catch (e) {
        console.warn('Failed to load sound settings from server:', e)
      }
    },

    // Set custom sound URL (서버에 저장)
    setCustomSoundUrl: async (url: string | null) => {
      set({ customSoundUrl: url })
      // 서버에 저장
      try {
        const { soundSettings, chatUnreadAlarm } = get()
        await apiService.put('/api/v1/admin/notices/sound-settings', {
          custom_sound_url: url,
          sound_settings: soundSettings,
          chat_unread_alarm: {
            enabled: chatUnreadAlarm.enabled,
            interval_minutes: chatUnreadAlarm.intervalMinutes,
          },
        })
        console.log('🔊 Custom sound URL saved to server:', url)
      } catch (e) {
        console.warn('Failed to save custom sound URL to server:', e)
      }
    },

    // 안읽은 채팅 반복 알람 설정 (서버에 저장)
    setChatUnreadAlarm: async (cfg: ChatUnreadAlarmConfig) => {
      const normalized: ChatUnreadAlarmConfig = {
        enabled: !!cfg.enabled,
        intervalMinutes: Math.max(1, Math.min(60, Number(cfg.intervalMinutes) || 1)),
      }
      set({ chatUnreadAlarm: normalized })
      try {
        const { customSoundUrl, soundSettings } = get()
        await apiService.put('/api/v1/admin/notices/sound-settings', {
          custom_sound_url: customSoundUrl,
          sound_settings: soundSettings,
          chat_unread_alarm: {
            enabled: normalized.enabled,
            interval_minutes: normalized.intervalMinutes,
          },
        })
        console.log('🔔 Chat unread alarm saved to server:', normalized)
      } catch (e) {
        console.warn('Failed to save chat unread alarm to server:', e)
      }
    },

    // 특정 알림 타입의 소리 설정 (서버에 저장)
    setSoundForType: async (type: string, url: string | null) => {
      const { soundSettings, customSoundUrl } = get()
      const newSettings = { ...soundSettings }
      
      if (url) {
        newSettings[type] = url
      } else {
        delete newSettings[type]
      }
      
      set({ soundSettings: newSettings })
      
      // 서버에 저장
      try {
        const { chatUnreadAlarm } = get()
        await apiService.put('/api/v1/admin/notices/sound-settings', {
          custom_sound_url: customSoundUrl,
          sound_settings: newSettings,
          chat_unread_alarm: {
            enabled: chatUnreadAlarm.enabled,
            interval_minutes: chatUnreadAlarm.intervalMinutes,
          },
        })
        console.log('🔊 Sound settings saved to server:', newSettings)
      } catch (e) {
        console.warn('Failed to save sound settings to server:', e)
      }
    },

    // 특정 알림 타입의 소리 가져오기
    getSoundForType: (type: string) => {
      const { soundSettings, customSoundUrl } = get()
      // 타입별 소리가 있으면 우선 사용, 없으면 기본 소리 사용
      return soundSettings[type] || customSoundUrl || null
    },
    
    // Play TTS notification sound
    playNotificationSound: (message: string, notificationType?: string) => {
      console.log('🔊 playNotificationSound called with message:', message, 'type:', notificationType)

      const { getSoundForType } = get()
      // 알림 타입별 소리 가져오기 (타입이 있으면 타입별, 없으면 기본)
      const soundUrl = notificationType ? getSoundForType(notificationType) : get().customSoundUrl
      
      // Fallback: 기본 브라우저 TTS (먼저 정의)
      const fallbackToBuiltinTTS = () => {
        if ('speechSynthesis' in window) {
          // 기존 TTS 취소
          window.speechSynthesis.cancel()
          
          // 약간의 딜레이 후 재생 (브라우저 정책 준수)
          setTimeout(() => {
            const playTTS = () => {
              try {
                const voices = window.speechSynthesis.getVoices()
                const utterance = new SpeechSynthesisUtterance(message)

                utterance.lang = 'ko-KR'
                utterance.rate = 1.0  // 속도 조정 (1.2 -> 1.0)
                utterance.pitch = 1.0  // 피치 조정 (1.5 -> 1.0)
                utterance.volume = 1.0  // 볼륨 최대

                // 한국어 음성 찾기
                const koreanVoices = voices.filter(voice => 
                  voice.lang.includes('ko') || voice.lang.includes('KR')
                )

                if (koreanVoices.length > 0) {
                  // 여성 음성 우선 선택
                  const femaleVoice = koreanVoices.find(voice =>
                    voice.name.includes('female') ||
                    voice.name.includes('Yuna') ||
                    voice.name.includes('child') ||
                    voice.name.includes('young')
                  )
                  utterance.voice = femaleVoice || koreanVoices[0]
                }

                utterance.onstart = () => {
                  console.log('🔊 TTS 시작:', message)
                }

                utterance.onend = () => {
                  console.log('🔊 TTS 종료')
                }

                utterance.onerror = (event) => {
                  console.warn('TTS error:', event)
                }

                console.log('🔊 TTS 재생 시도:', { message, voice: utterance.voice?.name, lang: utterance.lang })
                window.speechSynthesis.speak(utterance)
              } catch (e) {
                console.warn('TTS failed:', e)
              }
            }

            // 음성 목록이 준비되지 않았으면 대기
            if (window.speechSynthesis.getVoices().length === 0) {
              const voicesHandler = () => {
                window.speechSynthesis.onvoiceschanged = null
                playTTS()
              }
              window.speechSynthesis.onvoiceschanged = voicesHandler
              // 최대 1초 대기
              setTimeout(() => {
                if (window.speechSynthesis.getVoices().length > 0) {
                  window.speechSynthesis.onvoiceschanged = null
                  playTTS()
                }
              }, 1000)
            } else {
              playTTS()
            }
          }, 100)  // 100ms 딜레이
        } else {
          console.warn('TTS not supported in this browser')
        }
      }

      // 기본 소리 재생 함수 정의 (모든 탭/기기에서 재생)
      const playDefaultSound = () => {
        try {
          // 공유 AudioContext 사용 — 제스처로 이미 잠금해제된 컨텍스트라 백그라운드 탭에서도 재생됨
          const audioContext = getSharedCtx()
          if (!audioContext) return
          
          // 백그라운드 탭에서도 재생되도록 처리
          const playTones = () => {
            const playTone = (frequency: number, duration: number, delay: number = 0) => {
              setTimeout(() => {
                try {
                  // 매번 새로운 oscillator 생성 (백그라운드에서도 작동)
                  const oscillator = audioContext.createOscillator()
                  const gainNode = audioContext.createGain()
                  
                  oscillator.connect(gainNode)
                  gainNode.connect(audioContext.destination)
                  
                  oscillator.frequency.value = frequency
                  oscillator.type = 'sine'
                  
                  gainNode.gain.setValueAtTime(0.5, audioContext.currentTime)
                  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)
                  
                  oscillator.start(audioContext.currentTime)
                  oscillator.stop(audioContext.currentTime + duration)
                } catch (e) {
                  console.warn('Failed to play tone:', e)
                }
              }, delay)
            }
            
            // 명확한 3톤 알림 (도-미-솔) - 볼륨 증가
            playTone(523, 0.25, 0)
            playTone(659, 0.25, 250)
            playTone(784, 0.35, 500)
            
            console.log('🔔 Notification sound played on all devices:', message, 'Visibility:', document.visibilityState)
          }
          
          // 오디오 컨텍스트가 suspended 상태면 resume (백그라운드에서도 작동)
          if (audioContext.state === 'suspended') {
            audioContext.resume().then(() => {
              console.log('🔊 AudioContext resumed, playing sound on all devices')
              playTones()
            }).catch(e => {
              console.warn('Failed to resume AudioContext, trying anyway:', e)
              playTones()
            })
          } else {
            playTones()
          }
        } catch (e) {
          console.error('Failed to play notification sound:', e)
        }
      }
      
      // 다른 창/백그라운드일 때 OS 데스크톱 알림 배너 (리더 탭만 — 중복 방지)
      if (get().isLeaderTab) showDesktopNotification(message)

      // 모든 탭/기기에서 소리 재생, 리더 탭에서만 TTS 재생
      const { isLeaderTab } = get()
      
      // 커스텀 소리 파일이 있으면 그것만 재생 (TTS와 기본 소리 재생 안 함)
      if (soundUrl) {
        try {
          // 1순위: Web Audio 버퍼 재생 — 상호작용 없는 백그라운드 탭에서도 재생됨
          if (playUrlViaWebAudio(soundUrl)) {
            if (get().isLeaderTab) showDesktopNotification(message)
            return
          }
          // 폴백: HTMLAudio (버퍼 미로드 시 — 다음 알림부터는 버퍼로 재생됨)
          const audio = new Audio(soundUrl)
          audio.volume = 1.0  // 최대 볼륨으로 설정
          
          // 백그라운드에서도 재생되도록 처리
          const playAudio = () => {
            audio.play().then(() => {
              console.log('🔊 Custom sound file playing on all devices:', soundUrl, 'Visibility:', document.visibilityState)
            }).catch(e => {
              console.warn('Failed to play custom sound file, falling back to default:', e)
              // 재생 실패 시에만 기본 소리와 TTS 재생
              playDefaultSound()
              // 리더 탭에서만 TTS 재생
              if (isLeaderTab) {
                try {
                  fallbackToBuiltinTTS()
                } catch (e) {
                  console.warn('TTS fallback failed:', e)
                }
              }
            })
          }
          
          // 페이지가 보이지 않아도 재생 시도
          if (document.visibilityState === 'hidden') {
            // 백그라운드 탭에서도 재생되도록 약간의 딜레이
            setTimeout(playAudio, 50)
          } else {
            playAudio()
          }
          
          // 커스텀 소리가 있으면 그것만 재생하고 종료 (TTS 재생 안 함)
          return
        } catch (e) {
          console.warn('Failed to create audio from custom sound URL, falling back to default:', e)
          // 기본 소리 재생 (모든 탭/기기에서)
          playDefaultSound()
          // 리더 탭에서만 TTS 재생
          if (isLeaderTab) {
            try {
              fallbackToBuiltinTTS()
            } catch (e) {
              console.warn('TTS fallback failed:', e)
            }
          }
            }
          } else {
        // 기본 알림음 재생 (커스텀 소리가 없는 경우) - 모든 탭/기기에서 재생
        playDefaultSound()
        // 리더 탭에서만 TTS 재생
        if (isLeaderTab) {
      try {
        fallbackToBuiltinTTS()
      } catch (e) {
        console.warn('TTS fallback failed:', e)
          }
        }
      }
    },
    
    // Internal: Set notifications
    setNotifications: (notifications: Notification[]) => {
      const unreadCount = notifications.filter(n => !n.is_read).length
      set({ notifications, unreadCount })
    },
    
    // Internal: Add new notification (중복 체크)
    addNotification: (notification: Notification) => {
      set((state) => {
        // 중복 알림 체크: 같은 ID가 이미 있으면 추가하지 않음
        const existingNotification = state.notifications.find(n => n.id === notification.id)
        if (existingNotification) {
          console.log('🔔 Duplicate notification ignored:', notification.id, notification.title)
          return state
        }
        
        // 중복 체크: 같은 reference_id와 type이 있고, 5초 이내에 생성된 알림이면 무시
        const recentDuplicate = state.notifications.find(n => 
          n.reference_id === notification.reference_id &&
          n.type === notification.type &&
          n.reference_id !== null &&
          Math.abs(new Date(n.created_at).getTime() - new Date(notification.created_at).getTime()) < 5000
        )
        if (recentDuplicate) {
          console.log('🔔 Duplicate notification ignored (same reference_id and type within 5s):', notification.id, notification.title)
          return state
        }
        
        // 새 알림 추가
        return {
        notifications: [notification, ...state.notifications],
        unreadCount: notification.is_read ? state.unreadCount : state.unreadCount + 1
        }
      })
    },
    
    // Internal: Set connection status
    setConnectionStatus: (connected: boolean) => {
      set({ isConnected: connected })
    }
  }))
)

// Load voices when they become available
if ('speechSynthesis' in window) {
  window.speechSynthesis.onvoiceschanged = () => {
    console.log('🔊 TTS voices loaded:', window.speechSynthesis.getVoices().length)
  }
}